import {
  Download,
  DownloadFailedTemplate,
  DownloadTemplate,
  DownloadTemplateVM,
} from 'spw-programming-projects/downloads/types';
import { ActionsUnion, createAction } from 'spw-redux/types';
import { sanitizeFileName } from 'spw-shared/utils';
import {
  ESAReportAttributes,
  FileType,
  ProgrammingReportAttributes,
  ReportAttributes,
} from 'spw-redux/modules/reports/types';
import { MeasurementSystem } from 'spw-components/unit-conversion/types';

export const DOWNLOAD = 'downloads/DOWNLOAD';
export const PREPARE_AND_EXECUTE_REPORT_DOWNLOAD =
  'downloads/PREPARE_AND_EXECUTE_REPORT_DOWNLOAD';
export const PREPARE_AND_EXECUTE_ESA_REPORT_DOWNLOAD =
  'downloads/PREPARE_AND_EXECUTE_ESA_REPORT_DOWNLOAD';
export const PREPARE_AND_EXECUTE_PROGRAMMING_REPORT_DOWNLOAD =
  'downloads/PREPARE_AND_EXECUTE_PROGRAMMING_REPORT_DOWNLOAD';
export const PREPARE_AND_EXECUTE_BENCHMARK_REPORT_DOWNLOAD =
  'downloads/PREPARE_AND_EXECUTE_BENCHMARK_REPORT_DOWNLOAD';
export const DOWNLOAD_WORKSHEET_SPREADSHEET =
  'downloads/DOWNLOAD_WORKSHEET_SPREADSHEET';
export const REPORT_DOWNLOAD_SUCCEEDED = 'downloads/REPORT_DOWNLOAD_SUCCEEDED';
export const REPORT_DOWNLOAD_FAILED = 'downloads/REPORT_DOWNLOAD_FAILED';
export const DOWNLOAD_SUCCEEDED = 'downloads/DOWNLOAD_SUCCEEDED';
export const START_LOCAL_DOWNLOAD = 'downloads/START_LOCAL_DOWNLOAD';
export const GET_DOWNLOAD_TEMPLATES = 'downloads/GET_DOWNLOAD_TEMPLATES';
export const GET_DOWNLOAD_TEMPLATES_SUCCEEDED =
  'downloads/GET_DOWNLOAD_TEMPLATES_SUCCEEDED';
export const DELETE_DOWNLOAD_TEMPLATE = 'downloads/DELETE_DOWNLOAD_TEMPLATE';
export const DELETE_DOWNLOAD_TEMPLATE_SUCCEEDED =
  'downloads/DELETE_DOWNLOAD_TEMPLATE_SUCCEEDED';
export const GET_DOWNLOAD_TEMPLATE_DETAIL =
  'downloads/GET_DOWNLOAD_TEMPLATE_DETAIL';
export const GET_DOWNLOAD_TEMPLATE_DETAIL_SUCCEEDED =
  'downloads/GET_DOWNLOAD_TEMPLATE_DETAIL_SUCCEEDED';
export const CREATE_DOWNLOAD_TEMPLATE = 'downloads/CREATE_DOWNLOAD_TEMPLATE';
export const CREATE_DOWNLOAD_TEMPLATE_SUCCEEDED =
  'downloads/CREATE_DOWNLOAD_TEMPLATE_SUCCEEDED';
export const CREATE_DOWNLOAD_TEMPLATE_FAILED =
  'downloads/CREATE_DOWNLOAD_TEMPLATE_FAILED';
export const UPDATE_DOWNLOAD_TEMPLATE = 'downloads/UPDATE_DOWNLOAD_TEMPLATE';
export const UPDATE_DOWNLOAD_TEMPLATE_SUCCEEDED =
  'downloads/UPDATE_DOWNLOAD_TEMPLATE_SUCCEEDED';
export const UPDATE_DOWNLOAD_TEMPLATE_FAILED =
  'downloads/UPDATE_DOWNLOAD_TEMPLATE_FAILED';
export const DOWNLOAD_TEMPLATE = 'downloads/DOWNLOAD_TEMPLATE';
export const GET_DOWNLOADS = 'downloads/GET_DOWNLOADS';
export const CHECK_FOR_DOWNLOADS_IN_PROGRESS_SUCCEEDED =
  'downloads/CHECK_FOR_DOWNLOADS_IN_PROGRESS_SUCCEEDED';
export const GET_FINISHED_PROJECT_DOWNLOADS_SUCCEEDED =
  'downloads/GET_FINISHED_PROJECT_DOWNLOADS_SUCCEEDED';
export const CREATE_DOWNLOAD = 'downloads/CREATE_DOWNLOAD';
export const CREATE_DOWNLOAD_SUCCEEDED = 'downloads/CREATE_DOWNLOAD_SUCCEEDED';
export const CREATE_DOWNLOAD_FAILED = 'downloads/CREATE_DOWNLOAD_FAILED';
export const DELETE_DOWNLOAD = 'downloads/DELETE_DOWNLOAD';
export const CANCEL_DOWNLOADS_IN_PROGRESS =
  'downloads/CANCEL_DOWNLOADS_IN_PROGRESS';
export const GET_DOWNLOADS_IN_PROGRESS = 'downloads/GET_DOWNLOADS_IN_PROGRESS';
export const GET_DOWNLOADS_IN_PROGRESS_SUCCEEDED =
  'downloads/GET_DOWNLOADS_IN_PROGRESS_SUCCEEDED';
export const SET_FAILED_DOWNLOADS_TEMPLATES =
  'downloads/SET_FAILED_DOWNLOADS_TEMPLATES';
export const GET_NUMBER_OF_DOWNLOADS_IN_PROGRESS =
  'downloads/GET_NUMBER_OF_DOWNLOADS_IN_PROGRESS';
export const SET_NUMBER_OF_DOWNLOADS_IN_PROGRESS =
  'downloads/SET_NUMBER_OF_DOWNLOADS_IN_PROGRESS';
export const SET_TEMPLATES_WITH_DOWNLOADS_IN_PROGRESS =
  'downloads/SET_TEMPLATES_WITH_DOWNLOADS_IN_PROGRESS';

export const prepareAndExecuteReportDownload = (
  reportAttributes: ReportAttributes,
  zipName: string
) =>
  createAction(PREPARE_AND_EXECUTE_REPORT_DOWNLOAD, {
    reportAttributes,
    zipName: sanitizeFileName(zipName),
  });

export const prepareAndExecuteESAReportDownload = ({
  outputFileName,
  reportAttributes,
}: {
  outputFileName: string;
  reportAttributes: ESAReportAttributes;
}) =>
  createAction(PREPARE_AND_EXECUTE_ESA_REPORT_DOWNLOAD, {
    outputFileName: sanitizeFileName(outputFileName),
    reportAttributes,
  });

export const prepareAndExecuteProgrammingReportDownload = ({
  outputFileName,
  reportAttributes,
}: {
  outputFileName: string;
  reportAttributes: Partial<ProgrammingReportAttributes>;
}) =>
  createAction(PREPARE_AND_EXECUTE_PROGRAMMING_REPORT_DOWNLOAD, {
    outputFileName: sanitizeFileName(outputFileName),
    reportAttributes,
  });

export const prepareAndExecuteBenchmarkReportDownload = ({
  outputFileName,
  format,
  isIndividualCardsExport,
}: {
  outputFileName: string;
  format: FileType;
  isIndividualCardsExport?: boolean;
}) =>
  createAction(PREPARE_AND_EXECUTE_BENCHMARK_REPORT_DOWNLOAD, {
    outputFileName: sanitizeFileName(outputFileName),
    format,
    isIndividualCardsExport,
  });

export const downloadWorksheetSpreadsheet = (options: {
  rowsOpen: boolean;
  showDecimals: boolean;
  showClientFacingNames: boolean;
  measurementSystem: MeasurementSystem;
}) => createAction(DOWNLOAD_WORKSHEET_SPREADSHEET, options);

export const reportDownloadSucceeded = () =>
  createAction(REPORT_DOWNLOAD_SUCCEEDED);
export const reportDownloadFailed = () => createAction(REPORT_DOWNLOAD_FAILED);

export const download = (
  downloads: Record<string, unknown>[],
  zipName?: string
) =>
  createAction(DOWNLOAD, {
    downloads,
    zipName: sanitizeFileName(zipName),
  });

export const getDownloadTemplates = () => createAction(GET_DOWNLOAD_TEMPLATES);
export const getDownloadTemplatesSucceeded = (
  downloadTemplates: DownloadTemplate[]
) => createAction(GET_DOWNLOAD_TEMPLATES_SUCCEEDED, downloadTemplates);
export const getDownloadTemplateDetail = (id: number) =>
  createAction(GET_DOWNLOAD_TEMPLATE_DETAIL, id);
export const getDownloadTemplateDetailSucceeded = (
  downloadTemplate: DownloadTemplateVM
) => createAction(GET_DOWNLOAD_TEMPLATE_DETAIL_SUCCEEDED, downloadTemplate);
export const deleteDownloadTemplate = (id: number) =>
  createAction(DELETE_DOWNLOAD_TEMPLATE, id);
export const deleteDownloadTemplateSucceeded = () =>
  createAction(DELETE_DOWNLOAD_TEMPLATE_SUCCEEDED);
export const createDownloadTemplate = (
  template: DownloadTemplateVM,
  shouldStartDownloadAfterSaving: boolean,
  navigateBack: () => void
) =>
  createAction(CREATE_DOWNLOAD_TEMPLATE, {
    template,
    shouldStartDownloadAfterSaving,
    navigateBack,
  });
export const createDownloadTemplateSucceeded = () =>
  createAction(CREATE_DOWNLOAD_TEMPLATE_SUCCEEDED);
export const createDownloadTemplateFailed = () =>
  createAction(CREATE_DOWNLOAD_TEMPLATE_FAILED);
export const updateDownloadTemplate = (
  template: DownloadTemplateVM,
  navigateBack?: () => void,
  shouldDownloadAfterSaving?: boolean
) =>
  createAction(UPDATE_DOWNLOAD_TEMPLATE, {
    template,
    navigateBack,
    shouldDownloadAfterSaving,
  });
export const updateDownloadTemplateSucceeded = () =>
  createAction(UPDATE_DOWNLOAD_TEMPLATE_SUCCEEDED);
export const updateDownloadTemplateFailed = () =>
  createAction(UPDATE_DOWNLOAD_TEMPLATE_FAILED);
export const downloadTemplate = (
  { templateId, downloadName }: { templateId: number; downloadName?: string },
  navigateBack?: () => void
) =>
  createAction(DOWNLOAD_TEMPLATE, { templateId, downloadName, navigateBack });
export const createDownload = (
  name: string,
  template: DownloadTemplateVM,
  navigateBack?: () => void
) => createAction(CREATE_DOWNLOAD, { name, template, navigateBack });
export const createDownloadSucceeded = () =>
  createAction(CREATE_DOWNLOAD_SUCCEEDED);
export const createDownloadFailed = () => createAction(CREATE_DOWNLOAD_FAILED);
export const deleteDownload = (id: number) =>
  createAction(DELETE_DOWNLOAD, { id });
export const cancelDownloadsInProgress = () =>
  createAction(CANCEL_DOWNLOADS_IN_PROGRESS);
export const getDownloads = () => createAction(GET_DOWNLOADS);
export const getNumberOfDownloadsInProgress = () =>
  createAction(GET_NUMBER_OF_DOWNLOADS_IN_PROGRESS);
export const setNumberOfDownloadsInProgress = (
  numberOfDownloadInProgress: number
) =>
  createAction(SET_NUMBER_OF_DOWNLOADS_IN_PROGRESS, {
    numberOfDownloadInProgress,
  });

export const getFinishedProjectDownloadsSucceeded = (
  finishedProjectDownloads: Download[]
) =>
  createAction(GET_FINISHED_PROJECT_DOWNLOADS_SUCCEEDED, {
    finishedProjectDownloads,
  });
export const getDownloadsInProgress = () =>
  createAction(GET_DOWNLOADS_IN_PROGRESS);
export const getDownloadsInProgressSucceeded = (
  downloadsInProgress: Download[]
) => createAction(GET_DOWNLOADS_IN_PROGRESS_SUCCEEDED, { downloadsInProgress });
export const setTemplatesWithDownloadsInProgress = (templatesWithDownloadsInProgressMap: {
  [templateId: number]: boolean;
}) =>
  createAction(
    SET_TEMPLATES_WITH_DOWNLOADS_IN_PROGRESS,
    templatesWithDownloadsInProgressMap
  );
export const setFailedDownloadsTemplates = (
  failedDownloadsTemplates: DownloadFailedTemplate[]
) => createAction(SET_FAILED_DOWNLOADS_TEMPLATES, { failedDownloadsTemplates });

export const actions = {
  download,
  prepareAndExecuteReportDownload,
  getDownloadTemplateDetail,
  setFailedDownloadsTemplates,
};

export type Actions = ActionsUnion<typeof actions>;
